import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { PayMethods } from "@/components/PayMethods";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lock, School, Clock, Loader2, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";

const money = (n) => `R${Number(n || 0).toFixed(2)}`;

export function CollegesInner() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(false);
  const [selected, setSelected] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [code, setCode] = useState("");

  const load = (cc) => api.get(`/colleges/${cc}/courses`).then(({ data }) => setData(data)).catch(() => toast.error("Could not load college programmes."));
  useEffect(() => {
    api.get("/colleges").then(({ data }) => {
      const list = data.colleges || [];
      setColleges(list);
      const first = list[0]?.code;
      if (first) { setCode(first); load(first); api.post("/payments/reconcile").then(({ data: r }) => { if (r.reconciled > 0) load(first); }).catch(() => {}); }
    }).catch(() => toast.error("Could not load colleges."));
  }, []);
  const pickCollege = (cc) => { if (cc === code) return; setCode(cc); setSelected([]); setData(null); load(cc); };
  const Picker = () => (colleges.length > 1 ? (
    <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="college-picker">
      <span className="text-sm font-semibold text-[#0B2447]">College</span>
      <Select value={code} onValueChange={pickCollege}>
        <SelectTrigger className="w-full sm:w-80 border-slate-300" data-testid="college-select"><SelectValue /></SelectTrigger>
        <SelectContent>{colleges.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`college-option-${c.code}`}>{c.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  ) : null);

  const unlock = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/initialize", { type: "unlock", callback_url: `${window.location.origin}/payment/callback` });
      window.location.href = data.authorization_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); setPaying(false); }
  };

  const toggle = (id) => setSelected((cur) => {
    if (cur.includes(id)) return cur.filter((x) => x !== id);
    if (cur.length >= 3) { toast.error("You can choose up to 3 programmes per application."); return cur; }
    return [...cur, id];
  });

  const checkout = () => {
    if (!selected.length) { toast.error("Select at least one programme."); return; }
    const courses = data.courses.filter((c) => selected.includes(c.id));
    sessionStorage.setItem("yva_college_cart", JSON.stringify({ college: data.college, course_ids: selected, courses }));
    navigate("/dashboard/apply-college");
  };

  if (!data) return <div><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-[#0B2447]" /></div></div>;

  const fee = data.college?.application_fee || 85.99;

  if (!data.unlocked) {
    return (
      <div>
        <div className="max-w-2xl mx-auto px-6 py-16">
          <Picker />
          <div className="flex items-center gap-2 text-[#0B2447]"><School className="w-5 h-5 text-[#F5C32C]" /><span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{data.college?.name}</span></div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-6 bg-white border border-slate-200 rounded-2xl p-8 shadow-lg text-center" data-testid="college-unlock-card">
            <div className="w-14 h-14 rounded-xl bg-[#0B2447] flex items-center justify-center mx-auto"><Lock className="w-7 h-7 text-[#F5C32C]" /></div>
            <h1 className="font-display text-2xl font-bold text-[#0B2447] mt-5">Unlock {data.count} college programmes</h1>
            <p className="text-slate-600 mt-2 text-sm">Pay the once-off <span className="font-semibold text-[#0B2447]">R26.99</span> unlock fee to browse and apply to NC(V) &amp; N-Diploma programmes at {data.college?.name}.</p>
            <div className="flex flex-wrap gap-2 justify-center mt-4">{(data.categories || []).map((c) => <Badge key={c} variant="secondary" className="bg-[#0B2447]/5 text-[#0B2447]">{c}</Badge>)}</div>
            <PayMethods className="mt-6" />
            <Button onClick={unlock} disabled={paying} data-testid="college-unlock-btn" className="mt-6 w-full bg-[#F5C32C] hover:bg-[#E3B220] text-[#0B2447] font-bold py-6 text-base">
              {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" /> Unlock for R{Number(data.unlock_fee).toFixed(2)}</>}
            </Button>
            <p className="flex items-center justify-center gap-1.5 mt-4 text-xs text-slate-400"><ShieldCheck className="w-3.5 h-3.5" /> Secured by Paystack</p>
          </motion.div>
        </div>
      </div>
    );
  }

  const groups = {};
  data.courses.forEach((c) => { (groups[c.faculty] = groups[c.faculty] || []).push(c); });

  return (
    <div>
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Picker />
        <div className="flex items-center gap-3">
          <img src={data.college.logo_url} alt={data.college.name} className="w-12 h-12 rounded-lg object-contain bg-white border border-slate-100" />
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-[#0B2447]" data-testid="colleges-title">{data.college.name}</h1>
            <p className="text-slate-500 text-sm">Choose up to 3 programmes · one application is {money(fee)}</p>
          </div>
        </div>

        <div className="mt-8 space-y-6" data-testid="college-programmes">
          {Object.entries(groups).map(([cat, courses]) => (
            <div key={cat} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <div className="px-5 py-3 bg-[#0B2447]/[0.03] border-b border-slate-200"><h2 className="font-display font-bold text-[#0B2447]">{cat}</h2></div>
              <div className="divide-y divide-slate-100">
                {courses.map((c) => {
                  const checked = selected.includes(c.id);
                  return (
                    <label key={c.id} className={`flex items-start gap-3 p-4 cursor-pointer ${checked ? "bg-[#F5C32C]/[0.06]" : "hover:bg-slate-50"}`} data-testid={`college-row-${c.id}`}>
                      <Checkbox checked={checked} onCheckedChange={() => toggle(c.id)} disabled={!checked && selected.length >= 3} data-testid={`college-check-${c.id}`} className="mt-1" />
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold text-[#0B2447]">{c.name}</span>
                        <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-500">
                          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {c.level}</span>
                          {c.careers && <span className="truncate">{c.careers}</span>}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {selected.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-t border-slate-200 shadow-2xl" data-testid="college-cart-bar">
          <div className="max-w-4xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-4">
            <div><div className="font-display text-lg font-bold text-[#0B2447]">{selected.length} programme{selected.length !== 1 ? "s" : ""} selected</div><p className="text-xs text-slate-500">One application · {money(fee)}</p></div>
            <div className="flex items-center gap-4">
              <div className="font-display text-2xl font-bold text-[#0B2447]" data-testid="college-cart-total">{money(fee)}</div>
              <Button onClick={checkout} data-testid="college-checkout-btn" className="bg-[#F5C32C] hover:bg-[#E3B220] text-[#0B2447] font-bold py-6 px-8">Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Colleges() {
  return (
    <div className="min-h-screen bg-[#F8F9FA]"><Header /><CollegesInner /></div>
  );
}
